import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import logo from '../../../images/logo.svg';
import ipaLogo from '../../../images/IPA Logo (10).png';
import MainGrid from '../../styling/MainGrid';
import theme from '../../styling/theme';

const stickyNavigation = [
  { title: 'R&D Tax Credit', slug: '/tax-credit' },
  { title: 'Claims Process', slug: '/claims-process' },
  { title: 'Industry Sector', slug: '/industry-sector' },
  { title: 'Intellectual Property', slug: '/intellectual-property' },
  { title: 'Sponsorships', slug: '/sponsorships' },
  { title: 'About us', slug: '/about-us' },
  { title: 'Blog', slug: 'https://blog.rda.co.uk' },
  { title: 'Contact', slug: '/contact-us' },
];

interface NavigationProps {
  activeLink?: string;
}

interface StyledLinkProps {
  firstLink?: boolean;
  activeLink?: boolean;
}

const Navigation: FunctionComponent<NavigationProps> = ({ activeLink }) => {
  const basePath =
    process.env.BASE === '/' || !process.env.BASE ? '' : process.env.BASE;

  const isExternalLink = (url: string) => /^https?:\/\//i.test(url);

  return (
    <Container>
      <TopNav>
        <MainGrid noPaddingMobile noPaddingTablet>
          <TopNavInner>
            <LogoContainer>
              <a href={basePath || '/'}>
                <figure className="image">
                  <img src={logo} alt="R&D Logo" />
                </figure>
              </a>
            </LogoContainer>
          </TopNavInner>
        </MainGrid>
      </TopNav>

      <MainGrid noPaddingMobile>
        <StickyNav>
          {stickyNavigation.map((edge, key) => {
            const active = activeLink === edge.slug;

            if (isExternalLink(edge.slug)) {
              return (
                <ExternalStyledLink
                  href={edge.slug}
                  key={key}
                  target="_blank"
                  rel="noopener noreferrer"
                  firstLink={key === 0}
                  activeLink={active}
                >
                  {edge.title}
                </ExternalStyledLink>
              );
            }

            return (
              <StyledLink
                to={`${basePath}${edge.slug}`}
                key={key}
                firstLink={key === 0}
                activeLink={active}
              >
                {edge.title}
              </StyledLink>
            );
          })}

          <IpaNavLink
            href="https://ip-a.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit the IPA website"
          >
            <img src={ipaLogo} alt="IPA Logo" />
          </IpaNavLink>
        </StickyNav>
      </MainGrid>
    </Container>
  );
};

export default Navigation;

const Container = styled.nav`
  width: 100%;
  flex-direction: column;
  padding: 0 20px;
  display: none;

  @media screen and (min-width: 1024px) {
    padding: 0;
    display: flex;
  }
`;

const TopNav = styled.div`
  border-bottom: 1px solid ${theme.grey.keyline};
`;

const TopNavInner = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 3rem 0 1.3rem;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;

  a {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
  }

  figure {
    margin: 0;
  }

  img {
    height: 85px;
    width: auto;
    display: block;
  }
`;

const sharedLinkStyles = `
  color: ${theme.text.main};
  font-family: Roboto;
  font-size: 16px;
  line-height: 19px;
  cursor: pointer;
  position: relative;
  text-decoration: none !important;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-weight: 400;

  &:hover,
  &:active,
  &:focus {
    text-decoration: none !important;
  }
`;

const StyledLink = styled(({ firstLink, activeLink, ...props }) => (
  <Link {...props} />
))<StyledLinkProps>`
  &,
  &:link,
  &:visited {
    ${sharedLinkStyles}
    padding: ${props => (props.firstLink ? '0 2rem 0 0' : '0 2rem')};

    &::after {
      position: absolute;
      content: ' ';
      height: 3px;
      width: 80%;
      bottom: -1px;
      left: ${props => (props.firstLink ? '0' : '10%')};
      background: ${theme.brand.blue};
      transition: ease-out 0.3s;
      margin: 0 auto;
      transform: ${props => (props.activeLink ? 'scaleX(1)' : 'scaleX(0)')};
    }

    &:hover::after,
    &:active::after,
    &:focus::after {
      transform: scaleX(1);
    }
  }
`;

const ExternalStyledLink = styled(({ firstLink, activeLink, ...props }) => (
  <a {...props} />
))<StyledLinkProps>`
  &,
  &:link,
  &:visited {
    ${sharedLinkStyles}
    padding: ${props => (props.firstLink ? '0 2rem 0 0' : '0 2rem')};

    &::after {
      position: absolute;
      content: ' ';
      height: 3px;
      width: 80%;
      bottom: -1px;
      left: ${props => (props.firstLink ? '0' : '10%')};
      background: ${theme.brand.blue};
      transition: ease-out 0.3s;
      margin: 0 auto;
      transform: ${props => (props.activeLink ? 'scaleX(1)' : 'scaleX(0)')};
    }

    &:hover::after,
    &:active::after,
    &:focus::after {
      transform: scaleX(1);
    }
  }
`;

const StickyNav = styled.div`
  display: none;

  @media screen and (min-width: 1024px) {
    display: flex;
    align-items: center;
  }
`;

const IpaNavLink = styled.a`
  height: 60px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 0 2rem;
  text-decoration: none !important;

  img {
    height: 58px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  &:hover,
  &:active,
  &:focus {
    text-decoration: none !important;
  }
`;